import { Product, OlaggModule, PermissionEnum, Bonox, ProductsCodesBatchUpload } from "@olagg/db-types";
import { Button, Flex, Switch, Text, Box, Table, Thead, Tbody, Tr, Th, Td, TableContainer, Divider, VStack, Textarea, HStack, Badge, IconButton, useToast } from "@chakra-ui/react";

import OlaGGFormInput from "../../../components/Forms/OlaggFormInput";
import OlaggForm from "../../../components/Forms/OlaggForm";
import OlaggFormSelect from "../../../components/Forms/OlaggFormSelect";
import OlaggMarkdown from '@olagg/ui-kit/OlaggMarkdown';
import { useForm } from "react-hook-form";
import useRoles from "../../../features/Roles/hooks/useRoles";
import { yupResolver } from '@hookform/resolvers/yup';
import { ProductSchema, ProductData } from "@olagg/validation-schemas";
import { FC, useEffect, useState } from "react";
import useProduct from "./hooks/useProduct";

type IProps = {
  product?: Product | null;
  onSubmit: (data: ProductData, codes?: string[], file?: ProductsCodesBatchUpload) => void;
  isDuplicating?: boolean;
};

type Instructions = {
  es?: string;
  en?: string;
  pt?: string;
}

type Description = {
  es?: string;
  en?: string;
  pt?: string;
}

type DisplayName = {
  es?: string;
  en?: string;
  pt?: string;
}

const buildInstructions = (product?: Product) => ({
  es: product?.instructions || '',
  en: product?.extraData?.en?.instructions || '',
  pt: product?.extraData?.pt?.instructions || ''
});

const buildDescription = (product?: Product) => ({
  es: product?.description || '',
  en: product?.extraData?.en?.description || '',
  pt: product?.extraData?.pt?.description || ''
})

const buildDisplayName = (product?: Product) => ({
  es: product?.displayName || '',
  en: product?.extraData?.en?.displayName || '',
  pt: product?.extraData?.pt?.displayName || ''
});

const buildLinkText = (product?: Product) => ({
  es: product?.linkText || '',
  en: product?.extraData?.en?.linkText || '',
  pt: product?.extraData?.pt?.linkText || ''
})

const Form: FC<IProps> = ({ product, onSubmit, isDuplicating = false }) => {
  const { missingPermission } = useRoles();
  const toast = useToast();
  const {
    bonoxs,
    allBonoxs,
    isLoading: bonoxLoading,
    allCategories,
    categories,
    removeCode,
  } = useProduct();
  const [selectedBonox, setSelectedBonox] = useState<Bonox | null>(null);
  const [instructions, setInstructions] = useState<Instructions>(buildInstructions(product || undefined));
  const [description, setDescription] = useState<Description>(buildDescription(product || undefined));
  const [displayName, setDisplayName] = useState<DisplayName>(buildDisplayName(product || undefined));
  const [linkText, setLinkText] = useState(buildLinkText(product || undefined));
  const [otherLangs, setOtherLangs] = useState<boolean>(product?.extraData?.translated || false);

  const [hasTranslations, setHasTranslations] = useState<boolean>(
    !!(product?.extraData?.en || product?.extraData?.pt || product?.extraData?.translated)
  );

  const checkForTranslations = () => {
    const hasEnglish = !!(instructions.en || description.en || displayName.en || linkText.en);
    const hasPortuguese = !!(instructions.pt || description.pt || displayName.pt || linkText.pt);
    const hasExistingTranslations = !!(product?.extraData?.en || product?.extraData?.pt);
    
    return hasEnglish || hasPortuguese || hasExistingTranslations;
  };

  useEffect(() => {
    setHasTranslations(checkForTranslations());
  }, [instructions, description, displayName, linkText]);

  const useFormMethods = useForm<ProductData>({
    resolver: yupResolver(ProductSchema),
    mode: 'onChange',
    defaultValues: {
      name: product?.name || '',
      display_name: product?.displayName || '',
      slug: product?.slug || '',
      redeemable_region: product?.redeemableRegion || '',
      face_value: product?.faceValue || '',
      selling_price: product?.sellingPrice || 0,
      selling_currency: product?.sellingCurrency || '',
      merchant_price: product?.merchantPrice || 0,
      merchant_currency: product?.merchantCurrency || '',
      commission: product?.commission || '',
      markup_percentage: product?.markupPercentage || '',
      in_stock: product?.inStock || false,
      visible: product?.visible || false,
      source: product?.source || 'BonoxProduct',
      reference_id: isDuplicating ? undefined : (product?.referenceId || 0),
      image: product?.image || '',
      link: product?.link || null,
      link_text: product?.linkText || null,
      description: product?.description || '',
      instructions: product?.instructions || '',
      category: product?.category || '',
      requiredLevel: product?.requiredLevel || null,
      order: product?.order || 0,
      extra_data: product?.extraData || null
    }
  });

  useEffect(() => {
    allBonoxs({});
    allCategories();
  }, []);

  // Si hay un producto, encontrar el bonox correspondiente
  useEffect(() => {
    if (product && product.referenceId && bonoxs.length > 0 && !isDuplicating) {
      const correspondingBonox = bonoxs.find(b => b.id === product.referenceId);
      if (correspondingBonox) {
        setSelectedBonox(correspondingBonox);
      }
    }
  }, [product, bonoxs, isDuplicating]);

  // Mapear bonoxs para el selector usando reference_id
  const bonoxOptions = bonoxs.map(bonox => ({
    value: bonox.id,
    label: `${bonox.name} - ${bonox.id} - ${bonox.face_value} - ${bonox.redeemable_region}`,
  }));

  // Watch para detectar cambios en reference_id y source
  const watchReferenceId = useFormMethods.watch('reference_id');
  const watchSource = useFormMethods.watch('source');
  const isLocalProduct = watchSource === 'LocalProduct';

  // Efecto para auto-completar cuando cambia reference_id (solo BonoxProduct)
  useEffect(() => {
    if (isLocalProduct) return;
    if (watchReferenceId && bonoxs.length > 0) {
      const selected = bonoxs.find(b => b.id === watchReferenceId);
      if (selected) {
        setSelectedBonox(selected);

        // Auto-completar campos con información de bonox
        useFormMethods.setValue('name', selected.name);
        useFormMethods.setValue('redeemable_region', selected.redeemable_region);
        useFormMethods.setValue('face_value', selected.face_value);
        useFormMethods.setValue('selling_price', selected.selling_price);
        useFormMethods.setValue('selling_currency', selected.selling_currency);
        useFormMethods.setValue('merchant_price', selected.merchant_price);
        useFormMethods.setValue('merchant_currency', selected.merchant_currency);
        useFormMethods.setValue('commission', selected.commission);
        useFormMethods.setValue('in_stock', selected.in_stock);
        useFormMethods.setValue('source', 'BonoxProduct');

        // Si no hay nombre personalizado, usar el del bonox
        if (isDuplicating || !useFormMethods.getValues('name') || useFormMethods.getValues('name') === selected.name) {
          useFormMethods.setValue('name', selected.name);
        }
      }
    }
  }, [watchReferenceId, bonoxs, useFormMethods, product, isDuplicating, isLocalProduct]);

  // Reset campos al cambiar a LocalProduct (solo si no estamos editando un LocalProduct existente)
  useEffect(() => {
    if (isLocalProduct) {
      setSelectedBonox(null);
      // reference_id no aplica para LocalProduct, lo dejamos en 0
      if (useFormMethods.getValues('reference_id') !== 0) {
        useFormMethods.setValue('reference_id', 0);
      }
      // commission no aplica
      if (!useFormMethods.getValues('commission')) {
        useFormMethods.setValue('commission', '0');
      }
    }
  }, [isLocalProduct]);

  const beforeSubmit = (data: ProductData) => {
    const finalData = {
      ...data,
      extra_data: {
        ...data.extra_data,
        ...(hasTranslations && {
          en: {
            instructions: instructions.en || product?.extraData?.en?.instructions || '',
            description: description.en || product?.extraData?.en?.description || '',
            displayName: displayName.en || product?.extraData?.en?.displayName || '',
            linkText: linkText.en || product?.extraData?.en?.linkText || ''
          },
          pt: {
            instructions: instructions.pt || product?.extraData?.pt?.instructions || '',
            description: description.pt || product?.extraData?.pt?.description || '',
            displayName: displayName.pt || product?.extraData?.pt?.displayName || '',
            linkText: linkText.pt || product?.extraData?.pt?.linkText || ''
          },
          translated: true
        })
      }
    };
    onSubmit(finalData);
  };

  const getSellingPrice = () => {
    const markupPercentage = useFormMethods.watch('markup_percentage');
    const formMerchantPrice = useFormMethods.watch('merchant_price');
    const merchantPrice = isLocalProduct
      ? Number(formMerchantPrice)
      : (selectedBonox?.merchant_price || product?.merchantPrice);

    if (!merchantPrice) {
      return '-';
    }

    if (!markupPercentage) {
      return `${merchantPrice} USDC`;
    }

    const markup = parseFloat(markupPercentage.toString());

    if (isNaN(markup)) {
      return `${merchantPrice} USDC`;
    }

    let sellingPrice;

    if (markup === 0) {
      sellingPrice = merchantPrice;
    } else {
      const multiplier = calculatePriceMultiplier(markup);
      sellingPrice = merchantPrice * multiplier;
    }

    return `${sellingPrice} USDC`;
  };

  // Función auxiliar corregida para replicar EXACTAMENTE la lógica del backend
  const calculatePriceMultiplier = (markupPercentage: number) => {
    if (markupPercentage >= 0.0001 && markupPercentage <= 0.9999) {
      return markupPercentage;
    } else if (markupPercentage >= 1.0) {
      return markupPercentage;
    } else {
      return 1.0;
    }
  };

  // Función para obtener información del tipo de operación
  const getPriceOperationType = () => {
    const markup = parseFloat(useFormMethods.watch('markup_percentage')?.toString() || '0');

    if (!markup || markup === 0) {
      return {
        type: 'none',
        message: 'Sin factor aplicado',
        color: 'gray.400'
      };
    }

    if (markup >= 0.0001 && markup <= 0.9999) {
      const discountPercent = ((1 - markup) * 100).toFixed(2);
      return {
        type: 'discount',
        message: `🔻 Descuento del ${discountPercent}%`,
        color: 'green.300'
      };
    }

    if (markup >= 1.0) {
      const markupPercent = ((markup - 1) * 100).toFixed(2);
      return {
        type: 'markup',
        message: `🔺 Markup del ${markupPercent}%`,
        color: 'orange.300'
      };
    }

    return {
      type: 'invalid',
      message: 'Factor inválido',
      color: 'red.400'
    };
  };

  return (
    <Flex w={'60vw'} mx={4} pb={4} minH='600px' justifyContent='center' direction={"column"}>
      {isDuplicating && (
        <Box mb={4} p={3} bg="orange.100" borderLeft="4px solid orange.400" borderRadius="md">
          <Text color="orange.800" fontWeight="600">
            ⚠️ Duplicando producto
          </Text>
          <Text color="orange.700" fontSize="sm">
            Selecciona un nuevo producto de Bonox y ajusta los campos necesarios. 
            Los campos ya están pre-llenados con los datos del producto original.
          </Text>
        </Box>
      )}
      <OlaggForm
        onSubmit={beforeSubmit}
        useFormMethods={useFormMethods}
        style={{
          width: '100%'
        }}
      >
        {/* Switch para otros idiomas */}
        <Flex
          w="full"
          justify="start"
          align="center"
          style={{ marginBottom: '20px' }}
        >
          <label
            htmlFor="enableShowLocale"
            style={{ cursor: 'pointer', color: 'white' }}
          >
            Otros idiomas
          </label>
          <Switch
            id="enableShowLocale"
            colorScheme="purple"
            isChecked={otherLangs}
            size="md"
            ml={2}
            onChange={() => setOtherLangs(prev => !prev)}
          />
        </Flex>

        {/* Selector de tipo de producto */}
        <OlaggFormSelect
          label="Tipo de producto"
          controlName={'source'}
          staticOptions={[
            { value: 'BonoxProduct', label: 'BonoxProduct (inventario BONOXS)' },
            { value: 'LocalProduct', label: 'LocalProduct (inventario OLAGG)' },
          ]}
          required={true}
          newVersion
          placeholder="Selecciona el tipo de producto"
        />

        {/* Selector de Bonox - Solo para BonoxProduct */}
        {!isLocalProduct && (
          <OlaggFormSelect
            label="Seleccionar Bonox"
            controlName={'reference_id'}
            staticOptions={bonoxOptions}
            required={true}
            newVersion
            isSearchable
            placeholder={
              isDuplicating
                ? "Selecciona un nuevo producto de Bonox para la copia"
                : "Selecciona un producto de Bonox"
            }
          />
        )}

        {/* Campos editables para inventario propio (LocalProduct) */}
        {isLocalProduct && (
          <Box mb={6} w="full" bgColor="#20173d" p="6" borderRadius="12px">
            <Text fontSize="lg" fontWeight="bold" mb={4} color="white">
              Datos del producto (inventario OLAGG)
            </Text>
            <OlaGGFormInput
              label='Nombre interno'
              controlName='name'
              placeholder='Ej: Steam Wallet 50 USD'
              required={true}
            />
            <OlaGGFormInput
              label='Valor nominal (face value)'
              controlName='face_value'
              placeholder='Ej: USD 50'
              required={true}
            />
            <OlaggFormSelect
              label='Región de canje'
              controlName='redeemable_region'
              staticOptions={[
                'Worldwide',
                'Argentina',
                'Bolivia',
                'Brazil',
                'Chile',
                'Colombia',
                'Mexico',
                'Paraguay',
                'Peru',
                'United States',
                'Uruguay',
              ].map(r => ({ value: r, label: r }))}
              required={true}
              newVersion
              isSearchable
              placeholder='Selecciona una región'
            />
            <OlaGGFormInput
              label='Precio del comerciante (costo)'
              description='Costo configurable manualmente. Se usa en analytics.'
              controlName='merchant_price'
              placeholder='0.00'
              required={true}
            />
            <OlaGGFormInput
              label='Moneda del comerciante'
              controlName='merchant_currency'
              placeholder='USDC'
              required={true}
            />
            <OlaGGFormInput
              label='Moneda de venta'
              controlName='selling_currency'
              placeholder='USDC'
              required={true}
            />
          </Box>
        )}

        {/* Tabla con información del Bonox seleccionado */}
        {!isLocalProduct && (selectedBonox || product) && (
          <Box mb={6} w="full">
            <Text fontSize="lg" fontWeight="bold" mb={3} color="white">
              Información del Producto Bonox
            </Text>
            <TableContainer>
              <Table variant="simple" size="sm" bg="whiteAlpha.100" borderRadius="md">
                <Thead>
                  <Tr>
                    <Th color="white" borderColor="whiteAlpha.300">Campo</Th>
                    <Th color="white" borderColor="whiteAlpha.300">Valor</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td color="white" borderColor="whiteAlpha.200">Nombre en Bonox</Td>
                    <Td color="white" borderColor="whiteAlpha.200">
                      {selectedBonox?.name || product?.name || '-'}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td color="white" borderColor="whiteAlpha.200">Bonox ID</Td>
                    <Td color="white" borderColor="whiteAlpha.200">
                      {selectedBonox?.id || product?.referenceId || '-'}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td color="white" borderColor="whiteAlpha.200">Región de Canje</Td>
                    <Td color="white" borderColor="whiteAlpha.200">
                      {selectedBonox?.redeemable_region || product?.redeemableRegion || '-'}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td color="white" borderColor="whiteAlpha.200">Valor Nominal</Td>
                    <Td color="white" borderColor="whiteAlpha.200">
                      {selectedBonox?.face_value || product?.faceValue || '-'}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td color="white" borderColor="whiteAlpha.200">Precio de Venta</Td>
                    <Td color="white" borderColor="whiteAlpha.200">
                      {selectedBonox?.selling_price || product?.sellingPrice || '-'} {selectedBonox?.selling_currency || product?.sellingCurrency || ''}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td color="white" borderColor="whiteAlpha.200">Precio del Comerciante</Td>
                    <Td color="white" borderColor="whiteAlpha.200">
                      {selectedBonox?.merchant_price || product?.merchantPrice || '-'} {selectedBonox?.merchant_currency || product?.merchantCurrency || ''}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td color="white" borderColor="whiteAlpha.200">Comisión</Td>
                    <Td color="white" borderColor="whiteAlpha.200">
                      {selectedBonox?.commission || product?.commission || '-'}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td color="white" borderColor="whiteAlpha.200">Stock</Td>
                    <Td color="white" borderColor="whiteAlpha.200">
                      {selectedBonox?.in_stock !== undefined ? (selectedBonox.in_stock ? 'Disponible' : 'No disponible') : 
                       product?.inStock !== undefined ? (product.inStock ? 'Disponible' : 'No disponible') : '-'}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td color="white" borderColor="whiteAlpha.200">Fuente</Td>
                    <Td color="white" borderColor="whiteAlpha.200">
                      {selectedBonox ? 'BonoxProduct' : product?.source || '-'}
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Campos editables */}
        <OlaGGFormInput
          label='Nombre del Producto en la Tienda'
          controlName='display_name'
          placeholder='Nombre personalizado del producto'
          required={true}
          onChange={(e) => setDisplayName({ ...displayName, es: e.target.value })}
        />

        {/* Nombre en otros idiomas */}
        {otherLangs && (
          <Flex direction='column' w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4}>
            {/* Nombre en inglés */}
            <OlaGGFormInput
              label='Nombre en Inglés'
              controlName='extra_data.en.displayName'
              placeholder='Nombre in English'
              inputType='textarea'
              onChange={(e) => setDisplayName({ ...displayName, en: e.target.value })}
            />

            {/* Nombre en portugués */}
            <OlaGGFormInput
              label='Nombre en Portugués'
              controlName='extra_data.pt.displayName'
              placeholder='Nombre em Português'
              inputType='textarea'
              onChange={(e) => setDisplayName({ ...displayName, pt: e.target.value })}
            />
          </Flex>
        )}

        <OlaGGFormInput
          label='Slug del Producto'
          controlName='slug'
          placeholder='Slug del producto'
        />

        <OlaGGFormInput
          label='Descripción'
          controlName='description'
          placeholder='Descripción del producto'
          inputType='textarea'
          required={true}
          onChange={(e) => setDescription({ ...description, es: e.target.value })}
        />

        {/* Descripcion en otros idiomas */}
        {otherLangs && (
          <Flex direction='column' w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4}>
            {/* Descripcion en inglés */}
            <OlaGGFormInput
              label='Descripción en Inglés'
              controlName='extra_data.en.description'
              placeholder='Description in English'
              inputType='textarea'
              onChange={(e) => setDescription({ ...description, en: e.target.value })}
            />

            {/* Descripcion en portugués */}
            <OlaGGFormInput
              label='Descripción en Portugués'
              controlName='extra_data.pt.description'
              placeholder='Descripcao em Português'
              inputType='textarea'
              onChange={(e) => setDescription({ ...description, pt: e.target.value })}
            />
          </Flex>
        )}

        <OlaGGFormInput
          label='Factor de Margen o Descuento'
          description='menor que 1.0 = descuento. mayor a 1.0 = margen'
          controlName='markup_percentage'
          placeholder='0.0'
          required={true}
        />

        <Box 
          w="full" 
          mt={2} 
          mb={4} 
          p={3} 
          bg="whiteAlpha.100" 
          borderRadius="md" 
          borderLeft="4px solid"
        >
          <VStack align="start" spacing={2}>
            <HStack spacing={3}>
              <Text fontSize="md" color="white" fontWeight="600">
                Precio Final Calculado:
              </Text>
              <Text 
                fontSize="md" 
                fontWeight="700"
              >
                {getSellingPrice()}
              </Text>
            </HStack>

            {/* Información adicional sobre el cálculo */}
            {(() => {
              const operationInfo = getPriceOperationType();
              const formMerchantPrice = useFormMethods.watch('merchant_price');
              const merchantPrice = isLocalProduct
                ? Number(formMerchantPrice)
                : (selectedBonox?.merchant_price || product?.merchantPrice);

              return (
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" color="gray.400">
                    Precio base: ${merchantPrice || 0}
                  </Text>
                  <Text fontSize="xs" color={operationInfo.color}>
                    {operationInfo.message}
                  </Text>
                </VStack>
              );
            })()}
          </VStack>
        </Box>

        <OlaGGFormInput
          label='Orden de Visualización'
          controlName='order'
          placeholder='0'
          required={true}
        />

        <OlaGGFormInput
          label='Gratis a partir del nivel'
          controlName='requiredLevel'
          placeholder='dejar vacio si no se aplica'
          required={false}
        />

        <OlaGGFormInput
          label='URL de Imagen'
          controlName='image'
          placeholder='https://example.com/image.jpg'
          required={true}
        />

        <OlaGGFormInput
          label='Link del Producto'
          controlName='link'
          placeholder='https://example.com/product'
          required={false}
        />

        <OlaGGFormInput
          label='Texto del Link'
          controlName='link_text'
          placeholder='Texto del link del producto'
          required={false}
          onChange={(e) => setLinkText({ ...linkText, es: e.target.value })}
        />
        {/* Texto del link en otros idiomas */}
        {otherLangs && (
          <Flex direction='column' w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4}>
            {/* Texto del link en inglés */}
            <OlaGGFormInput
              label='Texto del Link en Inglés'
              controlName='extra_data.en.linkText'
              placeholder='Link Text in English'
              onChange={(e) => setLinkText({ ...linkText, en: e.target.value })}
            />

            {/* Texto del link en portugués */}
            <OlaGGFormInput
              label='Texto del Link en Portugués'
              controlName='extra_data.pt.linkText'
              placeholder='Texto do Link em Português'
              onChange={(e) => setLinkText({ ...linkText, pt: e.target.value })}
            />
          </Flex>
        )}

        <OlaggFormSelect
          label="Categoría del Producto"
          controlName={'category'}
          staticOptions={categories.map(cat => ({
            value: cat,
            label: cat
          }))}
          required={true}
          newVersion
          isSearchable
          placeholder="Selecciona una categoría"
        />

        {/* Instrucciones en español */}
        <OlaGGFormInput
          label='Instrucciones'
          controlName='instructions'
          description='Escribe las instrucciones de uso (puedes usar texto con Markdown)'
          placeholder='Instrucciones de uso del producto'
          inputType='textarea'
          required={true}
          onChange={(e) => setInstructions({ ...instructions, es: e.target.value })}
        />

        {/* Vista previa de instrucciones en español */}
        <Text lineHeight='normal' fontWeight={600} mb='2' color='white'>
          Instrucciones (Vista previa)
        </Text>
        <Box w='full' mb='5' border='1px solid white' minH='10' borderRadius='5' p='3'>
          <OlaggMarkdown>
            {instructions.es || product?.instructions || ''}
          </OlaggMarkdown>
        </Box>

        {/* Instrucciones en otros idiomas */}
        {otherLangs && (
          <Flex direction='column' w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4}>
            {/* Instrucciones en inglés */}
            <OlaGGFormInput
              label='Instrucciones en Inglés'
              controlName='extra_data.en.instructions'
              placeholder='Instructions in English'
              inputType='textarea'
              onChange={(e) => setInstructions({ ...instructions, en: e.target.value })}
            />

            <Text lineHeight='normal' fontWeight={600} mb='2' color='white'>
              Instrucciones Inglés (Vista previa)
            </Text>
            <Box w='full' mb='5' border='1px solid white' minH='10' borderRadius='5' p='3'>
              <OlaggMarkdown>
                {instructions.en || product?.extraData?.en?.instructions || ''}
              </OlaggMarkdown>
            </Box>

            {/* Instrucciones en portugués */}
            <OlaGGFormInput
              label='Instrucciones en Portugués'
              controlName='extra_data.pt.instructions'
              placeholder='Instruções em Português'
              inputType='textarea'
              onChange={(e) => setInstructions({ ...instructions, pt: e.target.value })}
            />

            <Text lineHeight='normal' fontWeight={600} mb='2' color='white'>
              Instrucciones Portugués (Vista previa)
            </Text>
            <Box w='full' mb='5' border='1px solid white' minH='10' borderRadius='5' p='3'>
              <OlaggMarkdown>
                {instructions.pt || product?.extraData?.pt?.instructions || ''}
              </OlaggMarkdown>
            </Box>
          </Flex>
        )}

        <OlaGGFormInput
          label='Visible'
          controlName='visible'
          inputType='switch'
          required={false}
        />

        {/* Campos ocultos: para BonoxProduct se autocompletan desde el Bonox seleccionado.
            Para LocalProduct los visibles arriba ya están registrados — sólo registramos los que
            siempre son derivados (selling_price, commission, in_stock, reference_id). */}
        {!isLocalProduct && (
          <>
            <input type="hidden" {...useFormMethods.register('name')} />
            <input type="hidden" {...useFormMethods.register('redeemable_region')} />
            <input type="hidden" {...useFormMethods.register('face_value')} />
            <input type="hidden" {...useFormMethods.register('selling_currency')} />
            <input type="hidden" {...useFormMethods.register('merchant_price')} />
            <input type="hidden" {...useFormMethods.register('merchant_currency')} />
          </>
        )}
        <input type="hidden" {...useFormMethods.register('selling_price')} />
        <input type="hidden" {...useFormMethods.register('commission')} />
        <input type="hidden" {...useFormMethods.register('in_stock')} />

        <Button
          variant={'filledGradient'}
          type="submit"
          w={'full'}
          style={{
            marginTop: '30px'
          }}
          isDisabled={
            missingPermission(OlaggModule.Product, product ? PermissionEnum.EDIT : PermissionEnum.CREATE) ||
            (!product && !isLocalProduct && !selectedBonox)
          }
        >
          {isDuplicating ? 'Crear Copia' : 'Guardar'}
        </Button>
      </OlaggForm>
    </Flex>
  )
}

export default Form;